
import { Options, Vue } from 'vue-class-component'
import { PropSync, Prop, Emit } from 'vue-property-decorator'
import IconRfid from '@/components/svg/IconRfid.vue'
import RfidPowerSwitch from '@/components/bizBrowser/RfidPowerSwitch.vue'
import { SendToBizBrowser } from '@/utils/bizBrowser/sendToBizBrowser'
import emitter from '@/utils/mitt'
import { RfidScanParsedResult } from '@/utils/bizBrowser/rfidScanResultHandler'
import { format } from 'date-fns'

type RfidItem = RfidScanParsedResult & {
  scannedAt: Date
  count: number
}

@Options({
  components: {
    IconRfid,
    RfidPowerSwitch,
  },
  emits: ['update:isShow', 'confirm'],
  name: 'RfidItemSelector',
})
export default class RfidItemSelector extends Vue {
  @PropSync('isShow', { type: Boolean }) _isShow!: boolean
  @Prop({ type: String, default: 'single-select' }) readonly mode!: 'single-select' | 'multiple-select'

  rfidMap: Map<string, RfidItem> = new Map()
  currentTime: Date = new Date()
  updateTimeInterval?: number
  selectedEpcs: Set<string> = new Set()

  get isStatusScanning() {
    return this.$store.getters.bizBrowserState.isDeviceScanning
  }

  get rfidList(): RfidItem[] {
    return Array.from(this.rfidMap.values())
  }

  get sortedRfidList(): RfidItem[] {
    return [...this.rfidList].sort((a, b) => {
      const rssiA = parseFloat(a.rssi)
      const rssiB = parseFloat(b.rssi)
      return rssiB - rssiA
    })
  }

  get isAllSelected(): boolean {
    return this.rfidList.length > 0 && this.selectedEpcs.size === this.rfidList.length
  }

  onDialogOpened() {
    SendToBizBrowser.startScan()
    emitter.on('rfid:scanned', this.handleScannedResults)
    this.updateTimeInterval = window.setInterval(() => {
      this.currentTime = new Date()
    }, 1000)
  }

  onDialogClosed() {
    SendToBizBrowser.idleMode()
    emitter.off('rfid:scanned', this.handleScannedResults)
    if (this.updateTimeInterval) {
      clearInterval(this.updateTimeInterval)
      this.updateTimeInterval = undefined
    }
    this.rfidMap.clear()
    this.selectedEpcs.clear()
  }

  toggleScanning() {
    if (this.isStatusScanning) {
      SendToBizBrowser.idleMode()
    } else {
      SendToBizBrowser.startScan()
    }
  }

  handleScannedResults(results?: RfidScanParsedResult[]) {
    if (!results) {
      return
    }
    results.forEach((result) => this.receiveScannedResult(result))
  }

  receiveScannedResult(result: RfidScanParsedResult) {
    const existingItem = this.rfidMap.get(result.epc)

    if (existingItem) {
      existingItem.rssi = result.rssi
      existingItem.scannedAt = new Date()
      existingItem.count++
    } else {
      this.rfidMap.set(result.epc, {
        ...result,
        scannedAt: new Date(),
        count: 1,
      })
    }

    this.rfidMap = new Map(this.rfidMap)
  }

  clearList() {
    this.rfidMap.clear()
    this.selectedEpcs.clear()
  }

  isSelected(epc: string): boolean {
    return this.selectedEpcs.has(epc)
  }

  toggleSelection(epc: string) {
    if (this.mode === 'single-select') {
      this.selectedEpcs.clear()
      this.selectedEpcs.add(epc)
    } else {
      if (this.selectedEpcs.has(epc)) {
        this.selectedEpcs.delete(epc)
      } else {
        this.selectedEpcs.add(epc)
      }
    }
    this.selectedEpcs = new Set(this.selectedEpcs)
  }

  toggleSelectAll() {
    if (this.isAllSelected) {
      this.selectedEpcs.clear()
    } else {
      this.rfidList.forEach((item) => {
        this.selectedEpcs.add(item.epc)
      })
    }
    this.selectedEpcs = new Set(this.selectedEpcs)
  }

  get selectedItems(): RfidItem[] {
    return this.sortedRfidList.filter((item) => this.selectedEpcs.has(item.epc))
  }

  @Emit('confirm')
  emitConfirm() {
    return this.selectedItems
  }

  onConfirm() {
    this.emitConfirm()
    this._isShow = false
  }

  formatTime(date: Date): string {
    const diffInSeconds = Math.floor((this.currentTime.getTime() - date.getTime()) / 1000)

    if (diffInSeconds < 60) {
      return `${Math.max(diffInSeconds, 0)}${this.$t('seconds_ago')}`
    } else if (diffInSeconds < 5940) {
      const minutes = Math.floor(diffInSeconds / 60)
      return `${minutes}${this.$t('minutes_ago')}`
    } else {
      return format(date, 'HH:mm:ss')
    }
  }

  formatRssi(rssi: string): string {
    const rssiValue = parseFloat(rssi)
    return rssiValue.toFixed(1)
  }

  getRssiClass(rssi: string): string {
    const rssiValue = parseFloat(rssi)
    if (rssiValue >= -50) return 'rssi-excellent'
    if (rssiValue >= -60) return 'rssi-good'
    if (rssiValue >= -70) return 'rssi-fair'
    return 'rssi-weak'
  }
}
